/**
 * Simple I18n Message Compiler for CSP environments
 * Converts message strings to functions to avoid 'new Function' (eval) usage by Vue I18n Full Build
 */

export const compileMessages = function(messages) {
  if (typeof messages !== 'object' || messages === null) {
    return messages;
  }

  // Handle arrays correctly to preserve structure
  const compiled = Array.isArray(messages) ? [] : {};

  for (const key in messages) {
    const value = messages[key];

    if (typeof value === 'string') {
      compiled[key] = compile(value);
    } else if (typeof value === 'object' && value !== null) {
      compiled[key] = compileMessages(value);
    } else {
      compiled[key] = value;
    }
  }

  return compiled;
};

/**
 * Compiles a message string into a function that Vue I18n runtime can execute.
 * Supports basic interpolation: {key} and {0}
 * Returns an array of strings/values to support VNode interpolation in <i18n-t>
 */
const compile = function(source) {
  // Optimization: static messages
  if (!source.includes('{') && !source.includes('@')) {
    return () => source;
  }

  return ctx => {
    const { named, list, linked } = ctx;
    const tokens = [];
    let lastIndex = 0;

    // Regex to match {0} (list) first, then {key} (named), or @:key
    // This prevents {0} from being matched as a named key since [a-zA-Z0-9_-] includes digits
    const regex = /\{\s*(\d+)\s*\}|\{([a-zA-Z0-9_-]+)\}|@:([a-zA-Z0-9_.-]+)/g;
    let match;

    while ((match = regex.exec(source)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        tokens.push(source.slice(lastIndex, match.index));
      }

      if (match[1]) {
        // {0} -> list interpolation (Group 1 now)
        const idx = parseInt(match[1]);
        const val = list(idx);

        if (Array.isArray(val)) {
          tokens.push(...val);
        } else {
          tokens.push(val !== undefined ? val : match[0]);
        }
      } else if (match[2]) {
        // {key} -> named interpolation (Group 2 now)
        const val = named(match[2]);
        if (Array.isArray(val)) {
          tokens.push(...val);
        } else {
          tokens.push(val !== undefined ? val : match[0]);
        }
      } else if (match[3]) {
        // @:key -> linked message
        const val = linked(match[3]);
        if (Array.isArray(val)) {
          tokens.push(...val);
        } else {
          tokens.push(val !== undefined ? val : match[0]);
        }
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < source.length) {
      tokens.push(source.slice(lastIndex));
    }

    // Return array if multiple tokens, or single value if just one
    // Vue I18n runtime handles array returns for component interpolation
    // FIX: vue-i18n legacy mode t() expects string, so we must join if all tokens are strings
    if (tokens.every(t => typeof t === 'string' || typeof t === 'number')) {
      return tokens.join('');
    }
    return tokens;
  };
};
